import { changeMetaThemeColor } from '@theme/utilities';

const OBSERVER_LIFETIME_MS = 20_000;
const SIZE_CHART_MODAL_CONFIGS = [
  { id: 'Avada-SC__Modal', openClass: 'Avada-Modal__Show' },
  { id: 'Avada-SC-modal', openClass: 'modal-show' },
];
const SIZE_CHART_MODAL_IDS = SIZE_CHART_MODAL_CONFIGS.map(({ id }) => id);
const SIZE_CHART_MODAL_SELECTOR = SIZE_CHART_MODAL_IDS.map((id) => `#${id}`).join(',');
const SIZE_CHART_OPEN_ATTRIBUTE = 'data-size-chart-open';
const WHITE_THEME_COLOR = '#ffffff';
const WHITE_BROWSER_CHROME_OVERLAY_ATTRIBUTES = [
  'data-mobile-menu-open',
  'data-cart-drawer-open',
  SIZE_CHART_OPEN_ATTRIBUTE,
];

let observedSizeChartModals = [];
const sizeChartModalObserver = new MutationObserver(connectSizeChartObservers);
const sizeChartDiscoveryObserver = new MutationObserver(handleSizeChartDiscoveryMutations);

function syncBrowserChromeColor() {
  const whiteOverlayIsOpen = WHITE_BROWSER_CHROME_OVERLAY_ATTRIBUTES.some((attribute) =>
    document.documentElement.hasAttribute(attribute)
  );
  const defaultThemeColor = document.querySelector('#header-component')?.dataset.themeColor || WHITE_THEME_COLOR;

  changeMetaThemeColor(whiteOverlayIsOpen ? WHITE_THEME_COLOR : defaultThemeColor);
}

function syncSizeChartBrowserChrome() {
  const isOpen = observedSizeChartModals.some(
    ({ element, openClass }) => element?.classList.contains(openClass) === true
  );
  document.documentElement.toggleAttribute(SIZE_CHART_OPEN_ATTRIBUTE, isOpen);
  syncBrowserChromeColor();
}

function connectSizeChartObservers() {
  const modals = SIZE_CHART_MODAL_CONFIGS.map(({ id, openClass }) => ({
    element: document.getElementById(id),
    openClass,
  }));
  const rootsChanged = modals.some(({ element }, index) => element !== observedSizeChartModals[index]?.element);

  if (rootsChanged) {
    sizeChartModalObserver.disconnect();
    observedSizeChartModals = modals;

    modals.forEach(({ element }) => {
      if (element) {
        sizeChartModalObserver.observe(element, { attributes: true, attributeFilter: ['class', 'id'] });
      }
    });
  }

  syncSizeChartBrowserChrome();
}

function handleSizeChartDiscoveryMutations(mutations) {
  const modalTreeChanged = mutations.some((mutation) => {
    if (
      mutation.type === 'attributes' &&
      mutation.target instanceof Element &&
      (SIZE_CHART_MODAL_IDS.includes(mutation.target.id) || SIZE_CHART_MODAL_IDS.includes(mutation.oldValue || ''))
    ) {
      return true;
    }

    return [...mutation.addedNodes, ...mutation.removedNodes].some(
      (node) =>
        node instanceof Element &&
        (SIZE_CHART_MODAL_IDS.includes(node.id) || node.querySelector(SIZE_CHART_MODAL_SELECTOR))
    );
  });

  if (modalTreeChanged) connectSizeChartObservers();
}

function observeSizeChartModal() {
  connectSizeChartObservers();

  if (document.body) {
    sizeChartDiscoveryObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['id'],
      attributeOldValue: true,
      childList: true,
      subtree: true,
    });
  }
}

function parseLocalizedPrice(value) {
  if (!value) return null;

  const sanitized = value.replace(/[\s\u00a0]/g, '').replace(/[^\d.,-]/g, '');
  const sign = sanitized.startsWith('-') ? -1 : 1;
  const unsigned = sanitized.replace(/-/g, '');

  if (!/\d/.test(unsigned)) return null;

  const lastComma = unsigned.lastIndexOf(',');
  const lastDot = unsigned.lastIndexOf('.');
  let decimalIndex = -1;

  if (lastComma >= 0 && lastDot >= 0) {
    decimalIndex = Math.max(lastComma, lastDot);
  } else {
    const separatorIndex = Math.max(lastComma, lastDot);
    const decimalPlaces = separatorIndex >= 0 ? unsigned.length - separatorIndex - 1 : 0;

    if (decimalPlaces > 0 && decimalPlaces <= 2) {
      decimalIndex = separatorIndex;
    }
  }

  const integerPart = (decimalIndex >= 0 ? unsigned.slice(0, decimalIndex) : unsigned).replace(/[.,]/g, '');
  const decimalPart = decimalIndex >= 0 ? unsigned.slice(decimalIndex + 1).replace(/[.,]/g, '') : '';
  const normalized = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
  const price = Number(normalized) * sign;

  return Number.isFinite(price) ? price : null;
}

function updateGemPagesDiscounts() {
  document.querySelectorAll('.gm-single-product').forEach((card) => {
    const label = card.querySelector('.gm-sale-label');
    const currentPriceElement = card.querySelector('.gm-price');
    const originalPriceElement = card.querySelector('.gm-old-price');

    if (!label || !currentPriceElement || !originalPriceElement) return;

    const currentPrice = parseLocalizedPrice(currentPriceElement.textContent);
    const originalPrice = parseLocalizedPrice(originalPriceElement.textContent);

    if (currentPrice === null || originalPrice === null || originalPrice <= 0) return;

    if (currentPrice >= originalPrice) {
      if (label.textContent) label.textContent = '';
      label.removeAttribute('aria-label');
      return;
    }

    const percentage = Math.round(((originalPrice - currentPrice) / originalPrice) * 100);
    const visibleLabel = `-${percentage}%`;
    const discountLabel = document.body.dataset.discountLabel || 'Discount';
    const accessibleLabel = `${discountLabel} ${percentage}%`;

    if (label.textContent.trim() !== visibleLabel) label.textContent = visibleLabel;
    if (label.getAttribute('aria-label') !== accessibleLabel) label.setAttribute('aria-label', accessibleLabel);
  });
}

function updateJudgeMeScore() {
  document.querySelectorAll('.jdgm-all-reviews-rating[data-score]').forEach((rating) => {
    const score = rating.getAttribute('data-score')?.trim();
    const scoreElement = rating.querySelector('.jdgm-all-reviews-rating__score');

    if (!score || !scoreElement) return;

    if (scoreElement.textContent.trim() !== score) scoreElement.textContent = score;
  });
}

function revealLoadedJudgeMeCarousels() {
  document.querySelectorAll('.jdgm-cards-carousel.jdgm-hidden').forEach((carousel) => {
    if (carousel.querySelector('.jdgm-card')) carousel.classList.remove('jdgm-hidden');
  });

  if (!document.querySelector('.jdgm-cards-carousel.jdgm-hidden')) {
    judgeMeObserver?.disconnect();
    judgeMeObserver = null;
  }
}

function observeHiddenJudgeMeCarousels() {
  const hiddenCarousels = Array.from(document.querySelectorAll('.jdgm-cards-carousel.jdgm-hidden')).filter(
    (carousel) => !carousel.querySelector('.jdgm-card')
  );

  if (!hiddenCarousels.length) {
    judgeMeObserver?.disconnect();
    judgeMeObserver = null;
    return;
  }

  judgeMeObserver ??= new MutationObserver(revealLoadedJudgeMeCarousels);
  judgeMeObserver.disconnect();
  hiddenCarousels.forEach((carousel) => {
    judgeMeObserver.observe(carousel.querySelector('.jdgm-cards-wrapper') || carousel, {
      childList: true,
      subtree: true,
    });
  });
}

function hasMatchingScript(pattern) {
  return Array.from(document.scripts).some((script) => pattern.test(script.src));
}

let animationFrame = 0;
let disconnectTimer = 0;
const observedTargets = new WeakSet();
const observer = new MutationObserver(scheduleEnhancements);
let announcementResizeObserver;
let announcementMutationObserver;
let judgeMeObserver;
let judgeMeScrollContainer;
let lastAnnouncementHeight = -1;

function connectJudgeMeScrollContainer() {
  const scrollContainer = document.querySelector('.page-wrapper');
  const scrollEvent = window.jdgm?.ScrollEvent;

  if (!(scrollContainer instanceof HTMLElement) || scrollContainer === judgeMeScrollContainer) return;
  if (!['auto', 'scroll'].includes(window.getComputedStyle(scrollContainer).overflowY)) return;
  if (typeof scrollEvent?.scrollInsideContainer !== 'function') return;

  scrollEvent.scrollInsideContainer('.page-wrapper');
  judgeMeScrollContainer = scrollContainer;
  scrollEvent.trigger?.('unveil');
}

function getAnnouncementStackElements() {
  const essentialCountdown = document.querySelector('.essential_countdown_annoucement_bar_wrapper');
  const nativeAnnouncement = document.querySelector('#header-group > .shopify-section:has(> .announcement-bar)');

  return [essentialCountdown, nativeAnnouncement].filter((element) => element instanceof HTMLElement);
}

function measureAnnouncementBarHeight() {
  const stackElements = getAnnouncementStackElements();
  const height = stackElements.reduce((total, element) => {
    const adjustment = element.matches('.essential_countdown_annoucement_bar_wrapper') ? -1 : 0;
    return total + Math.max(0, element.offsetHeight + adjustment);
  }, 0);

  if (height !== lastAnnouncementHeight) {
    lastAnnouncementHeight = height;
    document.documentElement.style.setProperty('--announcement-bar-height', `${height}px`);
  }
}

function syncAnnouncementBarHeight() {
  const stackElements = getAnnouncementStackElements();
  measureAnnouncementBarHeight();

  if (!('ResizeObserver' in window)) return;

  announcementResizeObserver ??= new ResizeObserver(measureAnnouncementBarHeight);
  announcementResizeObserver.disconnect();
  if (!stackElements.length) return;
  stackElements.forEach((element) => announcementResizeObserver.observe(element));

  if (stackElements.some((element) => element.matches('.essential_countdown_annoucement_bar_wrapper'))) {
    announcementMutationObserver?.disconnect();
  }
}

function discoverAnnouncementApps() {
  if (!document.body || announcementMutationObserver) return;

  announcementMutationObserver = new MutationObserver((mutations) => {
    const addedCountdown = mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some(
        (node) =>
          node instanceof Element &&
          (node.matches('.essential_countdown_annoucement_bar_wrapper') ||
            node.querySelector('.essential_countdown_annoucement_bar_wrapper'))
      )
    );

    if (addedCountdown) syncAnnouncementBarHeight();
  });
  announcementMutationObserver.observe(document.body, { childList: true, subtree: true });
  window.setTimeout(() => announcementMutationObserver?.disconnect(), OBSERVER_LIFETIME_MS);
}

function observeTarget(target) {
  if (!target || observedTargets.has(target)) return;

  observer.observe(target, { childList: true, subtree: true });
  observedTargets.add(target);

  if (!disconnectTimer) {
    disconnectTimer = window.setTimeout(() => observer.disconnect(), OBSERVER_LIFETIME_MS);
  }
}

function configureObservers() {
  const mainContent = document.getElementById('MainContent');
  const hasGemPages =
    document.querySelector('.gm-single-product, .gps') || hasMatchingScript(/gempages|gempage/i);
  const hasJudgeMe =
    document.querySelector('.jdgm-widget, [class*="jdgm-"]') || hasMatchingScript(/judge\.?me|judgeme|jdgm/i);

  if (mainContent && (hasGemPages || hasJudgeMe)) observeTarget(mainContent);
}

function runEnhancements() {
  updateGemPagesDiscounts();
  updateJudgeMeScore();
  revealLoadedJudgeMeCarousels();
  observeHiddenJudgeMeCarousels();
  connectJudgeMeScrollContainer();
  configureObservers();
}

function scheduleEnhancements() {
  if (animationFrame) return;

  animationFrame = window.requestAnimationFrame(() => {
    animationFrame = 0;
    runEnhancements();
  });
}

function start() {
  observeSizeChartModal();
  discoverAnnouncementApps();
  syncAnnouncementBarHeight();
  runEnhancements();

  document.addEventListener('shopify:section:load', scheduleEnhancements);
  document.addEventListener('shopify:section:load', syncAnnouncementBarHeight);
  document.addEventListener('shopify:section:unload', syncAnnouncementBarHeight);
  document.addEventListener('jdgm.finishLoadingCore', connectJudgeMeScrollContainer);
  window.addEventListener('pageshow', scheduleEnhancements);
  window.addEventListener('pageshow', syncAnnouncementBarHeight);
  window.addEventListener('pageshow', connectSizeChartObservers);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
